import { useTranslation } from "react-i18next";
import { Suspense, useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { cn } from "@/lib/core/utils";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "shrink-0 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
  );

/** Settings pages share a section nav on the left (on top on small screens). */
export function SettingsLayout() {
  const { t } = useTranslation();
  const nav = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // On phones the sections are a row that scrolls sideways: keep the current one in view.
  useEffect(() => {
    nav.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [pathname]);
  return (
    <div className="flex flex-col gap-8 lg:flex-row">
      <nav
        ref={nav}
        aria-label={t("settingsNav.label")}
        className="-mx-4 flex [scrollbar-width:none] gap-1 overflow-x-auto px-4 lg:mx-0 lg:w-56 lg:flex-none lg:flex-col lg:px-0"
      >
        <NavLink to="/settings" end className={navLinkClass}>
          {t("settingsNav.profile")}
        </NavLink>
        <NavLink to="/settings/company" className={navLinkClass}>
          {t("settingsNav.company")}
        </NavLink>
        <NavLink to="/settings/invoice" className={navLinkClass}>
          {t("settingsNav.invoice")}
        </NavLink>
        <NavLink to="/settings/payment" className={navLinkClass}>
          {t("settingsNav.payment")}
        </NavLink>
        <NavLink to="/settings/workspace" className={navLinkClass}>
          {t("settingsNav.workspace")}
        </NavLink>
        <NavLink to="/settings/plans" className={navLinkClass} data-testid="settings-nav-plans">
          {t("settingsNav.plan")}
        </NavLink>
        <NavLink to="/settings/apps" className={navLinkClass}>
          {t("settingsNav.apps")}
        </NavLink>
        <NavLink to="/settings/privacy" className={navLinkClass}>
          {t("settingsNav.privacy")}
        </NavLink>
      </nav>
      <div className="min-w-0 flex-1">
        {/* Keeps the settings nav on screen while a settings page's code loads. */}
        <Suspense fallback={<div className="min-h-[40vh]" aria-busy="true" />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}
